import { buildQueryParams, toQueryParams } from '@/helpers/ApiHelper'
import type { IPaginatedResponse } from '@/misc/IPaginatedResponse'
import type { ICaseFilter } from '@/models/CaseManagement/Filtering/ICaseFilter'
import type { IChargePointFilter } from '@/models/CaseManagement/Filtering/IChargePointFilter'
import type { IContactFilter } from '@/models/CaseManagement/Filtering/IContactFilter'
import type { ICaseAccount } from '@/models/CaseManagement/ICaseAccount'
import type { ICaseContacts } from '@/models/CaseManagement/ICaseContacts'
import type { ICaseCountry } from '@/models/CaseManagement/ICaseCountry'
import type { ICaseItemOrigin } from '@/models/CaseManagement/ICaseItemOrigin'
import type { ICaseManagement } from '@/models/CaseManagement/ICaseManagement'
import type { ICaseManagementCreate } from '@/models/CaseManagement/ICaseManagementCreate'
import type { ICaseOption } from '@/models/CaseManagement/ICaseOption'
import type { ISolutionCategory } from '@/models/CaseManagement/ISolutionCategory'
import type { IUpdateCase } from '@/models/CaseManagement/IUpdateCase'
import type { IChargePointDetails } from '@/models/Chargers/IChargePointDetails'
import type { IContact } from '@/models/Contact/IContact'
import type { IExcelColumn } from '@/models/Filtering/ExcelExportFilter'
import type { IBaseFilter } from '@/models/Filtering/IBaseFilter'
import type { IResultsPage } from '@/models/Pagination/IResultsPage'
import type { IVehicleFilter } from '@/models/Vehicle/Filtering/IVehicleFilter'
import type { IVehicle } from '@/models/Vehicle/IVehicle'
import { inject, injectable } from 'inversify'
import type IApiClient from '../IApiClient'
import type { IApiRequestOptions } from '../IApiRequestOptions'
import { SERVICES_TYPES } from '../ServicesTypes'
import type ICaseManagementService from './ICaseManagementService'

@injectable()
export class CaseManagementService implements ICaseManagementService {
  private readonly apiClient: IApiClient
  private readonly casesUri: string = 'cases'

  constructor(@inject(SERVICES_TYPES.ApiClient) apiClient: IApiClient) {
    this.apiClient = apiClient
  }

  async getCases(
    pageNumber?: number,
    pageSize?: number,
    sortField?: string,
    isAscending: boolean | undefined = undefined,
    caseOriginList?: string,
    contactName?: string,
    assignedTo?: string,
    servicePartyEscalated: boolean | undefined = undefined,
    dateFrom?: Date,
    dateTo?: Date,
    abortController?: AbortController
  ): Promise<IPaginatedResponse<ICaseManagement>> {
    const queryString = buildQueryParams({
      pageNumber,
      pageSize,
      sortField,
      isAscending,
      caseOriginList,
      contactName,
      assignedTo,
      servicePartyEscalated,
      dateFrom: dateFrom ? dateFrom.toISOString() : undefined,
      dateTo: dateTo ? dateTo.toISOString() : undefined
    })

    return this.apiClient.get<IPaginatedResponse<ICaseManagement>>(`${this.casesUri}${queryString}`, abortController)
  }

  async getCasesByChargePoint(
    chargePointId: string,
    pageNumber?: number,
    pageSize?: number,
    sortField?: string,
    isAscending: boolean | undefined = undefined,
    caseOriginList?: string,
    contactName?: string,
    assignedTo?: string,
    servicePartyEscalated: boolean | undefined = undefined,
    dateFrom?: Date,
    dateTo?: Date,
    abortController?: AbortController
  ): Promise<IPaginatedResponse<ICaseManagement>> {
    const queryString = buildQueryParams({
      pageNumber,
      pageSize,
      sortField,
      isAscending,
      caseOriginList,
      contactName,
      assignedTo,
      servicePartyEscalated,
      dateFrom: dateFrom ? dateFrom.toISOString() : undefined,
      dateTo: dateTo ? dateTo.toISOString() : undefined
    })

    return this.apiClient.get<IPaginatedResponse<ICaseManagement>>(`chargers/${chargePointId}/cases${queryString}`, abortController)
  }

  async getCaseByCaseNumber(caseNumber: string, abortController?: AbortController, options?: IApiRequestOptions): Promise<ICaseManagement> {
    return this.apiClient.get<ICaseManagement>(`cases/${caseNumber}`, abortController, options)
  }

  async getCaseOptions(): Promise<ICaseOption> {
    return this.apiClient.get<ICaseOption>(`case/options`)
  }

  async getCaseCountries(): Promise<IResultsPage<ICaseCountry>> {
    return this.apiClient.get<IResultsPage<ICaseCountry>>(`case/countries`)
  }

  async getCaseContactsByCustomerId(customerId: string): Promise<ICaseContacts> {
    return this.apiClient.get<ICaseContacts>(`cases/contacts/${customerId}`)
  }

  async getChargePoints(filter?: IChargePointFilter, abortController?: AbortController): Promise<IResultsPage<IChargePointDetails>> {
    const queryParam = filter ? toQueryParams<IChargePointFilter>(filter) : ''
    return this.apiClient.get<IResultsPage<IChargePointDetails>>(`case/chargepoints?${queryParam}`, abortController)
  }

  async getChargePointById(chargePointId: string): Promise<IChargePointDetails> {
    return this.apiClient.get<IChargePointDetails>(`case/chargepoints/${chargePointId}`)
  }

  async getContacts(filter?: IContactFilter, abortController?: AbortController): Promise<IResultsPage<IContact>> {
    const queryParam = filter ? toQueryParams<IContactFilter>(filter) : ''
    return this.apiClient.get<IResultsPage<IContact>>(`case/contacts?${queryParam}`, abortController)
  }

  async getCasesByCustomerId(contactId: string, caseFilter?: ICaseFilter, abortController?: AbortController): Promise<IResultsPage<ICaseManagement>> {
    const queryParam = caseFilter ? toQueryParams<ICaseFilter>(caseFilter) : ''
    return this.apiClient.get<IResultsPage<ICaseManagement>>(`contacts/${contactId}/cases?${queryParam}`, abortController)
  }

  async getSolutionByProblem(problemCategoryId: string, filter?: IBaseFilter, abortController?: AbortController): Promise<IResultsPage<ISolutionCategory>> {
    const queryParam = filter ? toQueryParams<IBaseFilter>(filter) : ''
    return this.apiClient.get<IResultsPage<ISolutionCategory>>(`problems/${problemCategoryId}/solutions?${queryParam}`, abortController)
  }

  async createCase(createCase: ICaseManagementCreate): Promise<undefined> {
    return this.apiClient.post<undefined>('cases', createCase)
  }

  async getVehicles(filter?: IVehicleFilter, abortController?: AbortController): Promise<IResultsPage<IVehicle>> {
    const queryParam = filter ? toQueryParams<IVehicleFilter>(filter) : ''
    return this.apiClient.get<IResultsPage<IVehicle>>(`case/vehicles?${queryParam}`, abortController)
  }

  async getAccountByIdentificationCode(identificationCode: string, abortController?: AbortController): Promise<ICaseAccount> {
    return this.apiClient.get<ICaseAccount>(`case/accounts/${identificationCode}`, abortController)
  }

  async getCaseOrigins(filter?: IBaseFilter): Promise<IResultsPage<ICaseItemOrigin>> {
    const queryString = buildQueryParams({
      pageIndex: filter?.PageIndex,
      pageSize: filter?.PageSize,
      sortColumn: filter?.SortColumn,
      sortOrder: filter?.SortOrder
    })
    return this.apiClient.get<IResultsPage<ICaseItemOrigin>>(`case/origins${queryString}`)
  }

  async exportCases(
    size: number,
    columnList: IExcelColumn[],
    sortField?: string,
    isAscending?: boolean,
    caseOriginList?: string,
    contactName?: string,
    assignedTo?: string,
    servicePartyEscalated?: boolean,
    dateFrom?: Date,
    dateTo?: Date,
    abortController?: AbortController
  ): Promise<{ blob: Blob; fileName?: string }> {
    const queryString = buildQueryParams({
      sortField,
      isAscending,
      caseOriginList,
      contactName,
      assignedTo,
      servicePartyEscalated,
      dateFrom: dateFrom ? dateFrom.toISOString() : undefined,
      dateTo: dateTo ? dateTo.toISOString() : undefined
    })

    return this.apiClient.postBlobWithFileName(`${this.casesUri}/download/${size}${queryString}`, columnList, abortController)
  }

  async exportCasesForChargePoint(
    chargePointId: string,
    size: number,
    columnList: IExcelColumn[],
    sortField?: string,
    isAscending?: boolean,
    caseOriginList?: string,
    contactName?: string,
    assignedTo?: string,
    servicePartyEscalated?: boolean,
    dateFrom?: Date,
    dateTo?: Date,
    abortController?: AbortController
  ): Promise<{ blob: Blob; fileName?: string }> {
    const queryString = buildQueryParams({
      sortField,
      isAscending,
      caseOriginList,
      contactName,
      assignedTo,
      servicePartyEscalated,
      dateFrom: dateFrom ? dateFrom.toISOString() : undefined,
      dateTo: dateTo ? dateTo.toISOString() : undefined
    })

    return this.apiClient.postBlobWithFileName(`chargers/${chargePointId}/${this.casesUri}/download/${size}${queryString}`, columnList, abortController)
  }

  async updateCase(updateCase: IUpdateCase): Promise<void> {
    return this.apiClient.put<void>(`${this.casesUri}/${updateCase.CaseNumber}`, updateCase)
  }
}
